import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MerckAuthService {
  private apiUrl = environment.apiUrl;
  private merckConfig = (environment as any).merckOidc;

  constructor(private http: HttpClient) {}

  loginWithMerck(loginHint?: string): Observable<any> {
    return from(this.initiateAuthorizationFlow(loginHint));
  }

  private async initiateAuthorizationFlow(loginHint?: string): Promise<any> {
    const authUrl = this.buildAuthorizationUrl(loginHint);

    return new Promise((resolve, reject) => {
      const popup = this.openPopup(authUrl, 'Merck Login');

      if (!popup) {
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      const pollTimer = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(pollTimer);
            reject(new Error('Login cancelled'));
            return;
          }

          const popupUrl = popup.location.href;
          if (popupUrl.startsWith(this.merckConfig.redirectUri)) {
            clearInterval(pollTimer);
            popup.close();

            const urlParams = new URLSearchParams(new URL(popupUrl).search);
            const code = urlParams.get('code');
            const error = urlParams.get('error');

            if (error) {
              reject(new Error(urlParams.get('error_description') || error));
              return;
            }

            if (!code) {
              reject(new Error('No authorization code received'));
              return;
            }

            this.exchangeCodeForTokens(code).subscribe({
              next: (result) => resolve(result),
              error: (err) => reject(err),
            });
          }
        } catch (_e) {
          // Cross-origin access - popup is on different domain, keep polling
        }
      }, 500);

      setTimeout(() => {
        clearInterval(pollTimer);
        if (!popup.closed) {
          popup.close();
        }
        reject(new Error('Login timeout'));
      }, 300000);
    });
  }

  private exchangeCodeForTokens(code: string): Observable<any> {
    return this.http.post(`${this.apiUrl}merck/callback`, {
      code: code,
      redirectUri: this.merckConfig.redirectUri,
    });
  }

  private buildAuthorizationUrl(loginHint?: string): string {
    const url = new URL(this.merckConfig.authorizationEndpoint);
    url.searchParams.append('client_id', this.merckConfig.clientId);
    url.searchParams.append('response_type', 'code');
    url.searchParams.append('scope', this.merckConfig.scopes);
    url.searchParams.append('redirect_uri', this.merckConfig.redirectUri);
    if (loginHint) {
      url.searchParams.append('login_hint', loginHint);
    }
    // This endpoint is a Keycloak realm - login_hint alone only pre-fills the
    // username field, it does NOT override an existing SSO session cookie for
    // a different account. prompt=login forces Keycloak to disregard any
    // active session and re-authenticate, so the email typed on our own login
    // page is actually the one that gets logged in (or prompted for), rather
    // than silently continuing whichever Merck account already has an active
    // browser session.
    url.searchParams.append('prompt', 'login');

    return url.toString();
  }

  private openPopup(url: string, title: string): Window | null {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    return window.open(
      url,
      title,
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
  }
}
